import {
  AppSettingsSchema,
  AuditEventSchema,
  BetaFeedbackSchema,
  CrashLogSchema,
  createAuditEvent,
  createDefaultSettings,
  DashboardSnapshotSchema,
  ExportBundleSchema,
  hydrateJob,
  IncidentSchema,
  JobSchema,
  LibraryCardSchema,
  migrateLegacyJob,
  migrateLegacyLibraryCard,
  todayDate,
  todayIso,
  type AppSettings,
  type AuditEvent,
  type BetaFeedback,
  type ChecklistKey,
  type CrashLog,
  type DashboardSnapshot,
  type ExportBundle,
  type Incident,
  type Job,
  type JobStatus,
  type LegacyBrowserSnapshot,
  type LibraryCard
} from '@shared/domain';
import { getFlow } from '@shared/flows';
import { parseIntakeText } from '@shared/intake';
import { LIBRARY_CARD_SEEDS } from '@shared/librarySeeds';

const STORAGE_KEYS = {
  jobs: 'sc-jobs-v2',
  cards: 'sc-library-v2',
  incidents: 'sc-incidents-v1',
  settings: 'sc-settings-v1',
  feedback: 'sc-beta-feedback-v1',
  crashLogs: 'sc-crash-logs-v1'
} as const;

const STORAGE_EVENT = 'service-companion:storage-change';
const STORAGE_LIMIT_BYTES = 5_000_000;

export class StorageQuotaError extends Error {
  constructor(message = 'Local storage is full. Export data or delete photos to free space.') {
    super(message);
    this.name = 'StorageQuotaError';
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function emitStorageChange(): void {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
}

export function subscribeStorageChanges(callback: () => void): () => void {
  if (!isBrowser()) {
    return () => undefined;
  }

  const handler = () => callback();
  window.addEventListener(STORAGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(STORAGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

function readValue<T>(key: string, parser: { parse: (value: unknown) => T }, fallback: T): T {
  if (!isBrowser()) return fallback;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return parser.parse(JSON.parse(raw));
  } catch {
    return fallback;
  }
}

function writeValue(key: string, value: unknown): void {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    emitStorageChange();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new StorageQuotaError();
    }
    throw error;
  }
}

function ensureSeedCards(): LibraryCard[] {
  const stored = readValue(STORAGE_KEYS.cards, LibraryCardSchema.array(), []);
  const merged = new Map(stored.map((card) => [card.id, card]));

  for (const seed of LIBRARY_CARD_SEEDS) {
    if (!merged.has(seed.id)) {
      merged.set(seed.id, LibraryCardSchema.parse(seed));
    }
  }

  const cards = [...merged.values()].toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  if (isBrowser() && (stored.length === 0 || stored.length !== cards.length)) {
    writeValue(STORAGE_KEYS.cards, cards);
  }
  return cards;
}

export function listJobsLocal(options: { query?: string; status?: JobStatus | 'All'; limit?: number } = {}): Job[] {
  const jobs = readValue(STORAGE_KEYS.jobs, JobSchema.array(), []).map((job) => hydrateJob(JobSchema.parse(job)));
  const filtered = jobs
    .filter((job) => {
      if (options.status && options.status !== 'All' && job.status !== options.status) return false;
      if (!options.query) return true;
      const query = options.query.toLowerCase();
      const haystack = [
        job.workOrder,
        job.address,
        job.customerName,
        job.phone,
        job.siteId,
        job.meterNumber,
        job.meterLocationDescription,
        job.autoComment,
        job.techNotes,
        ...job.riskNotes
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    })
    .toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));

  return typeof options.limit === 'number' ? filtered.slice(0, options.limit) : filtered;
}

export function getJobLocal(id: string): Job | null {
  return listJobsLocal().find((job) => job.id === id) ?? null;
}

export function saveJobLocal(job: Job): Job {
  const saved = hydrateJob(JobSchema.parse(job), { touch: true });
  const jobs = listJobsLocal({ limit: 10_000 });
  const index = jobs.findIndex((item) => item.id === saved.id);
  if (index >= 0) {
    jobs[index] = saved;
  } else {
    jobs.unshift(saved);
  }
  writeValue(STORAGE_KEYS.jobs, jobs);
  return saved;
}

export function deleteJobLocal(id: string): boolean {
  const jobs = listJobsLocal({ limit: 10_000 });
  const next = jobs.filter((job) => job.id !== id);
  if (next.length === jobs.length) return false;
  writeValue(STORAGE_KEYS.jobs, next);
  return true;
}

export function listLibraryCardsLocal(options: { query?: string; tag?: string; favorite?: boolean } = {}): LibraryCard[] {
  const cards = ensureSeedCards();
  return cards.filter((card) => {
    if (options.favorite && !card.favorite) return false;
    if (options.tag && !card.tags.includes(options.tag as LibraryCard['tags'][number])) return false;
    if (!options.query) return true;
    const query = options.query.toLowerCase();
    return [card.title, card.when, ...card.steps, ...card.fails, ...card.refs, ...card.tags].join(' ').toLowerCase().includes(query);
  });
}

export function saveLibraryCardLocal(card: LibraryCard): LibraryCard {
  const saved = LibraryCardSchema.parse({
    ...card,
    updatedAt: card.updatedAt || todayIso()
  });
  const cards = ensureSeedCards();
  const index = cards.findIndex((item) => item.id === saved.id);
  if (index >= 0) {
    cards[index] = saved;
  } else {
    cards.unshift(saved);
  }
  writeValue(STORAGE_KEYS.cards, cards);
  return saved;
}

export function deleteLibraryCardLocal(id: string): boolean {
  const cards = ensureSeedCards();
  const target = cards.find((card) => card.id === id);
  if (!target || target.system) return false;
  writeValue(
    STORAGE_KEYS.cards,
    cards.filter((card) => card.id !== id)
  );
  return true;
}

export function listIncidentsLocal(options: { query?: string; type?: Incident['type'] | 'All'; unresolvedOnly?: boolean } = {}): Incident[] {
  const incidents = readValue(STORAGE_KEYS.incidents, IncidentSchema.array(), []);
  return incidents
    .filter((incident) => {
      if (options.unresolvedOnly && incident.resolution) return false;
      if (options.type && options.type !== 'All' && incident.type !== options.type) return false;
      if (!options.query) return true;
      const query = options.query.toLowerCase();
      return [incident.type, incident.severity, incident.description, incident.resolution ?? '', ...incident.tags]
        .join(' ')
        .toLowerCase()
        .includes(query);
    })
    .toSorted((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export function saveIncidentLocal(incident: Incident): Incident {
  const saved = IncidentSchema.parse(incident);
  const incidents = listIncidentsLocal();
  const index = incidents.findIndex((item) => item.id === saved.id);
  if (index >= 0) {
    incidents[index] = saved;
  } else {
    incidents.unshift(saved);
  }
  writeValue(STORAGE_KEYS.incidents, incidents);
  return saved;
}

export function deleteIncidentLocal(id: string): boolean {
  const incidents = listIncidentsLocal();
  const next = incidents.filter((incident) => incident.id !== id);
  if (next.length === incidents.length) return false;
  writeValue(STORAGE_KEYS.incidents, next);
  return true;
}

export function listBetaFeedbackLocal(): BetaFeedback[] {
  return readValue(STORAGE_KEYS.feedback, BetaFeedbackSchema.array(), []).toSorted((left, right) =>
    right.timestamp.localeCompare(left.timestamp)
  );
}

export function saveBetaFeedbackLocal(feedback: BetaFeedback): BetaFeedback {
  const saved = BetaFeedbackSchema.parse(feedback);
  const entries = listBetaFeedbackLocal();
  const index = entries.findIndex((item) => item.id === saved.id);
  if (index >= 0) {
    entries[index] = saved;
  } else {
    entries.unshift(saved);
  }
  writeValue(STORAGE_KEYS.feedback, entries.slice(0, 100));
  return saved;
}

export function clearBetaFeedbackLocal(): void {
  writeValue(STORAGE_KEYS.feedback, []);
}

export function listCrashLogsLocal(): CrashLog[] {
  return readValue(STORAGE_KEYS.crashLogs, CrashLogSchema.array(), []).toSorted((left, right) =>
    right.timestamp.localeCompare(left.timestamp)
  );
}

export function saveCrashLogLocal(log: CrashLog): CrashLog {
  const saved = CrashLogSchema.parse(log);
  const entries = listCrashLogsLocal();
  entries.unshift(saved);
  writeValue(STORAGE_KEYS.crashLogs, entries.slice(0, 100));
  return saved;
}

export function clearCrashLogsLocal(): void {
  writeValue(STORAGE_KEYS.crashLogs, []);
}

export function getSettingsLocal(): AppSettings {
  return readValue(STORAGE_KEYS.settings, AppSettingsSchema, createDefaultSettings());
}

export function saveSettingsLocal(nextSettings: Partial<AppSettings>): AppSettings {
  const current = getSettingsLocal();
  const merged = AppSettingsSchema.parse({
    ...current,
    ...nextSettings
  });
  writeValue(STORAGE_KEYS.settings, merged);
  return merged;
}

export function recordTrainingReview(cardId: string): AppSettings {
  const settings = getSettingsLocal();
  const reviewedAt = todayIso();
  return saveSettingsLocal({
    trainingSeenCardIds: [...new Set([...settings.trainingSeenCardIds, cardId])],
    trainingReviewLog: [...settings.trainingReviewLog, { cardId, reviewedAt }].slice(-200)
  });
}

function recentReviewLookup(settings: AppSettings): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const entry of settings.trainingReviewLog) {
    const current = lookup.get(entry.cardId);
    if (!current || current < entry.reviewedAt) {
      lookup.set(entry.cardId, entry.reviewedAt);
    }
  }
  return lookup;
}

export function getDailyTrainingCardLocal(): LibraryCard | null {
  const settings = getSettingsLocal();
  const cards = ensureSeedCards();
  if (cards.length === 0) return null;

  if (settings.dailyCardDate === todayDate() && settings.dailyCardId) {
    return cards.find((card) => card.id === settings.dailyCardId) ?? cards.at(0) ?? null;
  }

  const lookup = recentReviewLookup(settings);
  const candidate = [...cards].sort((left, right) => {
    const leftSeen = lookup.get(left.id) ?? '';
    const rightSeen = lookup.get(right.id) ?? '';
    if (leftSeen !== rightSeen) return leftSeen.localeCompare(rightSeen);
    return left.title.localeCompare(right.title);
  })[0] ?? null;

  saveSettingsLocal({
    dailyCardDate: todayDate(),
    dailyCardId: candidate?.id ?? null
  });
  return candidate;
}

export function getWeeklyTrainingProgressLocal(): { reviewed: number; total: number } {
  const settings = getSettingsLocal();
  const cards = ensureSeedCards();
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const reviewed = new Set(
    settings.trainingReviewLog
      .filter((entry) => new Date(entry.reviewedAt).getTime() >= startOfWeek.getTime())
      .map((entry) => entry.cardId)
  );
  return {
    reviewed: reviewed.size,
    total: cards.length
  };
}

export function getDashboardSnapshotLocal(): DashboardSnapshot {
  const jobs = listJobsLocal({ limit: 1000 });
  const incidents = listIncidentsLocal({ unresolvedOnly: true });
  const recentSignals = [
    ...jobs.flatMap((job) => job.auditTrail),
    ...incidents.map((incident) =>
      createAuditEvent(
        'incident',
        `${incident.severity.toUpperCase()} ${incident.type} incident${incident.jobId ? ` on ${incident.jobId}` : ''}: ${incident.description}`,
        'system'
      )
    )
  ]
    .map((event) => AuditEventSchema.parse(event))
    .toSorted((left, right) => right.at.localeCompare(left.at))
    .slice(0, 10);

  const today = todayDate();
  const focusJobs = [...jobs]
    .sort((left, right) => {
      const leftFlow = getFlow(left.flowId);
      const rightFlow = getFlow(right.flowId);
      const leftRequired = leftFlow ? leftFlow.requiredChecklist.length : 0;
      const rightRequired = rightFlow ? rightFlow.requiredChecklist.length : 0;
      if (left.status !== right.status) {
        const order: JobStatus[] = ['Onsite', 'Open', 'Hold', 'Complete'];
        return order.indexOf(left.status) - order.indexOf(right.status);
      }
      if (leftRequired !== rightRequired) return rightRequired - leftRequired;
      return right.updatedAt.localeCompare(left.updatedAt);
    })
    .slice(0, 6);

  return DashboardSnapshotSchema.parse({
    jobCount: jobs.length,
    activeCount: jobs.filter((job) => job.status === 'Onsite').length,
    completedToday: jobs.filter((job) => job.status === 'Complete' && job.updatedAt.startsWith(today)).length,
    averageReadiness: jobs.length === 0 ? 0 : Math.round(jobs.reduce((sum, job) => sum + job.checklistCompletionPercent, 0) / jobs.length),
    statusBreakdown: ['Open', 'Onsite', 'Complete', 'Hold'].map((status) => ({
      status,
      count: jobs.filter((job) => job.status === status).length
    })),
    focusJobs,
    recentSignals
  });
}

export function exportAllDataLocal(): ExportBundle {
  const jobs = listJobsLocal({ limit: 10_000 });
  const cards = ensureSeedCards();
  const incidents = listIncidentsLocal();
  const auditEvents = jobs
    .flatMap((job) => job.auditTrail)
    .toSorted((left, right) => right.at.localeCompare(left.at))
    .slice(0, 5000);

  return ExportBundleSchema.parse({
    exportedAt: todayIso(),
    version: 'service-companion-v2',
    jobs,
    cards,
    incidents,
    settings: getSettingsLocal(),
    auditEvents,
    betaFeedback: listBetaFeedbackLocal(),
    crashLogs: listCrashLogsLocal()
  });
}

export function importLegacySnapshotLocal(snapshot: LegacyBrowserSnapshot): { jobsImported: number; cardsImported: number } {
  const jobs = snapshot.jobs.map(migrateLegacyJob);
  const cards = snapshot.cards.map(migrateLegacyLibraryCard);
  const mergedCards = new Map(ensureSeedCards().map((card) => [card.id, card]));

  for (const card of cards) {
    mergedCards.set(card.id, card);
  }

  writeValue(STORAGE_KEYS.jobs, jobs);
  writeValue(STORAGE_KEYS.cards, [...mergedCards.values()]);

  return {
    jobsImported: jobs.length,
    cardsImported: cards.length
  };
}

export function clearLocalData(): void {
  writeValue(STORAGE_KEYS.jobs, []);
  writeValue(
    STORAGE_KEYS.cards,
    ensureSeedCards().filter((card) => card.system)
  );
  writeValue(STORAGE_KEYS.incidents, []);
  writeValue(STORAGE_KEYS.settings, createDefaultSettings());
  writeValue(STORAGE_KEYS.feedback, []);
  writeValue(STORAGE_KEYS.crashLogs, []);
}

export function parseIntakeLocal(text: string) {
  return parseIntakeText(text);
}

export function getHealthLocal(): { ok: boolean; version: string; now: string } {
  return {
    ok: true,
    version: 'local-first-pwa',
    now: todayIso()
  };
}

export function getStorageUsageLocal(limitBytes = STORAGE_LIMIT_BYTES): {
  usedBytes: number;
  limitBytes: number;
  percent: number;
  nearingLimit: boolean;
} {
  if (!isBrowser()) {
    return { usedBytes: 0, limitBytes, percent: 0, nearingLimit: false };
  }

  let usedBytes = 0;
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key) continue;
    const value = window.localStorage.getItem(key) ?? '';
    usedBytes += (key.length + value.length) * 2;
  }

  const percent = Math.round((usedBytes / limitBytes) * 100);
  return {
    usedBytes,
    limitBytes,
    percent,
    nearingLimit: usedBytes / limitBytes >= 0.8
  };
}

export function getPhotoStorageSummaryLocal(): { count: number; bytes: number } {
  const jobs = listJobsLocal({ limit: 10_000 });
  const photos = jobs.flatMap((job) => job.photos);
  return {
    count: photos.length,
    bytes: photos.reduce((sum, photo) => sum + photo.sizeBytes, 0)
  };
}

export function buildJobTextSummary(job: Job): string {
  const flow = getFlow(job.flowId);
  const photoSummary = job.photos.length === 0 ? 'None' : job.photos.map((photo) => `${photo.name} (${Math.round(photo.sizeBytes / 1024)} KB)`).join(', ');
  const checklistLines = (Object.entries(job.checklist) as [ChecklistKey, boolean][])
    .map(([key, value]) => `- ${value ? '[x]' : '[ ]'} ${key.replaceAll('_', ' ')}`)
    .join('\n');

  return [
    `Job ${job.workOrder || job.id}`,
    `Status: ${job.status}`,
    `Flow: ${flow?.label ?? 'None'}`,
    `Address: ${job.address || 'Not set'}`,
    `Customer: ${job.customerName || 'Not set'}`,
    `Phone: ${job.phone || 'Not set'}`,
    `Site ID: ${job.siteId || 'Not set'}`,
    `Meter: ${job.meterNumber || 'Not set'}`,
    `Travel: ${job.travel.totalMinutes} minutes`,
    `Work: ${job.work.totalMinutes} minutes`,
    `Checklist completion: ${job.checklistCompletionPercent}%`,
    'Checklist:',
    checklistLines,
    '',
    `Closeout comment: ${job.autoComment || 'Not generated'}`,
    `Tech notes: ${job.techNotes || 'None'}`,
    `Risk notes: ${job.riskNotes.join(', ') || 'None'}`,
    `Photos: ${photoSummary}`,
    '',
    'Audit trail:',
    ...job.auditTrail.map((event) => `- ${event.at} ${event.type}: ${event.detail}`)
  ].join('\n');
}

export function collectAuditEventsForJobs(jobs: Job[]): AuditEvent[] {
  return jobs.flatMap((job) => job.auditTrail).toSorted((left, right) => right.at.localeCompare(left.at));
}
